<script setup>
import { ref, onBeforeUnmount, onMounted, inject, watch, nextTick} from 'vue';
import Grid from '@highcharts/grid-lite/es-modules/masters/grid-lite.src.js';
import '@highcharts/grid-lite/css/grid.css';
import markdownit from 'markdown-it'

import {defineTableProps, pageTableProps} from './GridTableSetup';
//import {defineTableProps} from '@/components/explorer/visualizations/table/GridTableSetup';
import PagingControls from './PagingControls.vue';
import TableOptions from "@/components/explorer/visualizations/table/GridTableOptions.vue"
import NoDataMsg from "@/components/common/NoDataMsg.vue"

//componenet methods exposed to parent
defineExpose({
	setupTable
});

//events raised by component
const emit = defineEmits(["openFilters"])
const { userInputState } = inject('userInputState') //holds state of has Year been set by user

//reactive field proxies
const gridContainer = ref(null);
const gridInstance = ref(null);
const tableTitle = ref('')
const currentPage=ref(1)
const totalPages=ref(0)
const showNoDataWarning=ref(false)
const footNotes = ref('')

//binding to invoke method on child componenet
const childRefPagingControl = ref({})
const childRefNoDataMsg = ref({})

//local variables
let chartSettings = null

//reactive field proxies
let indicator = ref({})

const md = markdownit()
let onFooterMoreClick;

//helper function
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

//Methods
function openFilters() {
	emit('openFilters')
}

async function setupTable(indicatorObj, settings, { onMoreClick }) {
	showNoDataWarning.value = false
	chartSettings = settings
	indicator.value = indicatorObj
	onFooterMoreClick = onMoreClick
	try {
		await defineTableProps(indicator.value, chartSettings, userInputState.value)
	} catch (err) {
		console.log(err)
		//display warning instead of table
		if(err.message == "No records found") {
			//sync UI page with paging info
			currentPage.value = 0
			totalPages.value = 0
			childRefPagingControl.value.initPaging(0,0)
			showNoDataWarning.value = true
			childRefNoDataMsg.value.setupMsg(indicator.value)
			return
		} else {
			throw err;
		}
	}
	//tableTitle.value = chartSettings.tableProps.title
	let title = `${chartSettings.tableProps.title}`
	let year = indicatorObj.columns.items.find(x => x.columnName == 'Year')
	if(year) {
		const minYear = Math.min(...year.appliedValues.filter(x => x.selected).map(item => Number(item.name)));
		const maxYear = Math.max(...year.appliedValues.filter(x => x.selected).map(item => Number(item.name)));
		if(minYear == maxYear)
			title += ": " + minYear
		else
			title += ": " + minYear + "\u2013" + maxYear
	}
	tableTitle.value = title



	//sync UI page which starts at 1 with API page array index which is 0 based
	currentPage.value = chartSettings.currentPage + 1
	totalPages.value = chartSettings.totalPages
	childRefPagingControl.value.initPaging(1, totalPages.value)

	const columnCount = chartSettings.tableProps.columns.length;
	const tableWidth = getTableWidth(columnCount);

	const container = document.getElementById("grid-container");
	container.style.minWidth = tableWidth + "px";
	if(gridInstance.value == null) {
		gridInstance.value = Grid.grid(gridContainer.value, chartSettings.tableProps);
		attachSortHandler();
	} else {
		gridInstance.value = Grid.grid(gridContainer.value, chartSettings.tableProps);
	}
/*
	const src = md.renderInline(indicator.value.source);
	//srcHtml is used in the footer mobile modal
	chartSettings.srcHtml = src
	let footer = `<div class="explorer-footnote-wrapper"><span class="explorer-footnote"><span class="title">Source(s):</span> ${src}
		</span><button class="btn-tertiary footnote-more" data-value='source'>
    		Full Source
  		</button></div>`
*/	
	const abbr = indicatorObj.abbreviations ? indicatorObj.abbreviations.join('; ') + '.' : '';
	const src = md.renderInline(indicatorObj.source);
	//srcHtml is used in the footer mobile modal
	chartSettings.srcHtml = src
	chartSettings.keyHtml = indicatorObj.abbreviations ? indicatorObj.abbreviations.join('.<br />') + '.' : '';

	let footerNotes = `<div class="explorer-footnote-wrapper">`
	if(indicatorObj.abbreviations && indicatorObj.abbreviations.length > 0) {
		footerNotes += `	<span class="explorer-footnote"><span class="title">Key:</span> ${abbr} </span>`
		footerNotes += `	<button class="btn-tertiary footnote-more" data-value='abbreviations'>Full Key</button><br />`
	}
	footerNotes += `	<button class="btn-tertiary explorer-footnote-link" data-value='source'>See full data notes</button><br/>`
	footerNotes += `	<span class="explorer-footnote"><span class="title">Source(s):</span> ${src} </span>`
	footerNotes += `	<button class="btn-tertiary footnote-more" data-value='source'>Full Source</button>`
	footerNotes += `</div>`
	footNotes.value = footerNotes
}

function getTableWidth(columnCount) {
	if (columnCount <= 6) return 1200;
	if (columnCount <= 8) return 1600;
	if (columnCount <= 9) return 2000;
	return 2400;
}

async function pageTable(ind, settings) {
	chartSettings = settings
	await pageTableProps(ind.value, chartSettings)
	chartSettings.tblProps
	//tableTitle.value = chartSettings.tableProps.title
	gridInstance.value = Grid.grid(gridContainer.value, chartSettings.tableProps);
}
async function waitForGridContainer(maxRetries = 20, interval = 100) {
	let retries = 0;
	// Loop while gridInstance, its value, or the container is missing
	while (!gridInstance.value?.container) {
		if (retries >= maxRetries) {
		console.error("Initialization timed out: gridInstance.value.container not found.");
		return null;
		}
		retries++;
		//console.log(`Waiting for grid container... Attempt ${retries}/${maxRetries}`);
		await delay(interval); // Pause execution before checking again
	}
	// Once the loop exits, the container is safely available
	return gridInstance.value.container;
}
// intercept sort clicks
async function attachSortHandler() {
	const container = await waitForGridContainer();
	if (container) {
		const nonSortableColumns = ["Notes"]
		gridInstance.value.container.addEventListener("click", e => {

			//const arrow = e.target.closest('.arrow');
			const arrow = e.target.closest('.btn-header-arrows');
			if (!arrow) return;

			//const header = e.target.closest(".highcharts-grid-column-header");
			const header = e.target.closest(".hcg-header-cell");
			if (!header) return;

			const columnId = header.dataset.columnId;
			if (!columnId) return;

			if (nonSortableColumns.includes(columnId)) {
				e.preventDefault()
				return
			}

			if (chartSettings.sortColumn.columnName === columnId) {
				chartSettings.sortDirection = chartSettings.sortDirection === "ASC" ? "DESC" : "ASC";
			} else {
				let newSortCol = indicator.value.columns.items.find(x => x.columnName === columnId)
				if(!newSortCol) {
					//add column to request:
					if(indicator.value.valuesLabel == columnId) {
						newSortCol =  {columnName: columnId, filters: [], sortDir: 'ASC', sortOrder: 1};
						chartSettings.request.columns.push(newSortCol);
					}
				}
				let col = chartSettings.request.columns.find(x => x.columnName === newSortCol.columnName)

				chartSettings.sortColumn = newSortCol;
				chartSettings.sortDirection = col && col.sortDir === "ASC" ? "DESC" : "ASC";
			}

			currentPage.value = 1;
			chartSettings.currentPage = 0
			chartSettings.request.page = 0

			pageTable(indicator, chartSettings);
		}, { passive: true });
	}
}

function updatePage(page) {
	//sync UI page which starts at 1 with API page array index which is 0 based
	currentPage.value = page;
	chartSettings.currentPage = page - 1
	chartSettings.request.page = page - 1

	pageTable(indicator, chartSettings);
}

onBeforeUnmount(() => {
	//console.log('unmount')
	if(gridInstance.value && gridInstance.value.container) {
		gridInstance.value.container.removeEventListener('click', attachSortHandler)
		gridInstance.value?.destroy()
	}
})

onMounted(() => {
	watch(footNotes, () => {

		//wait until DOM is updated, then attach listener
		nextTick(() => {
			const links = document.querySelectorAll('.footnote-more');
			if(links != null) {
				links.forEach(link => {
					if (link && !link.dataset.bound) {
						link.dataset.bound = 'true';

						link.addEventListener('click', (e) => {
							e.preventDefault();
							console.log('clicked', e)
							//alert('clicked')
							onFooterMoreClick(e)
						});
					}
				})
			}
			// adding an eventListener to "See full data notes" link
			const footerLinkElement = document.querySelector('.explorer-footnote-link')
			if (footerLinkElement) {
				footerLinkElement.addEventListener('click', () => {
					//gotoDataNotes()
					// footnote link onClick event fires a click event on the Data Notes menu item
					const menuItems = document.querySelector('.viz-menu-container').querySelectorAll('.menu-item')
					if(menuItems != null) {
						menuItems.forEach(item => {
							if (item.innerText.trim() == 'Data Notes') {
								item.click()
							}
						});
					}
				});
			}

		})

	})
})
</script>

<template>
	<div class="tbl-container highcharts-light">
		<div class="tbl-header-container">
			<TableOptions  @openFilters="openFilters" />
			<PagingControls ref="childRefPagingControl" @updatePage="updatePage" />

		</div>
		<div v-show="!showNoDataWarning">
			<div class="tbl-header highcharts-title">{{tableTitle}}</div>
			<div class="grid-wrapper">
  				<div id="grid-container" ref="gridContainer"></div>
			</div>
			<div class="table-footnote" v-html="footNotes"></div>
		</div>
	</div>
	<div v-show="showNoDataWarning" class="no-data-warning">
		<NoDataMsg :indicator="indicator" ref="childRefNoDataMsg" />
	</div>
</template>

<style>
/* Targets the internal classes generated by the Highcharts DataGrid */
.grid-wrapper .highcharts-datagrid-container,
.grid-wrapper table {
	width: 100% !important;
	max-width: 100% !important;
	table-layout: fixed; /* Prevents long textual content from expanding cell blocks */
}
.grid-wrapper {
	width: 100%;
	flex: 1 1 0%;
	overflow-x: auto;
	overflow-y: hidden;
	margin-bottom: .5rem;
  	/*max-height: 34vh; /* Or whatever explicit bounding height you need */
	/*min-height: 52vh;*/
	/*flex: 1 1 0%;
	min-width: 0;
	min-height: 0;
  	position: relative; /* Encourages proper layout context calculation */
	min-width: 0;      /* Crucial for row layouts to allow shrinking */

}

#grid-container {
	height: 42vh; /* 560px */
	/*display: flex;
  	flex-direction: column;
	flex: 1 1 0%;
	margin: 0;
	width: 100%;
	min-width: 0;
  	min-height: 0;
  	position: relative; /* Encourages proper layout context calculation */
	flex: 1 1 0%;
  	width: 100%;
	min-width: 0;      /* Crucial for row layouts to allow shrinking */
}

.tbl-container {
	display:flex;
	flex-direction: column;
	flex: 1 1 0%;
	min-width: 0;      /* Crucial for row layouts to allow shrinking */
  	/*min-height: 0;     /* Crucial for column layouts to allow shrinking */
  	/*overflow: hidden;  /* Collapses constraints, forcing Highcharts to adapt */
	width: 100%;
}

.tbl-header-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 0.75rem;
	min-width: 0;      /* Crucial for row layouts to allow shrinking */
}


.tbl-header, .tbl-external-header {
	color: var(--text-Header, #171717);
	text-align: center;
	/* Regular/H6 */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-H6, 1.25rem);
	font-style: normal;
	font-weight: 400;
	padding-bottom: 1rem;
	min-width: 0;      /* Crucial for row layouts to allow shrinking */
}

.tbl-spacer-row {
	height: 2.6875rem;
	background: var(--surface-Secondary, #F3F5F7);
	border: 1px solid var(--surface-Secondary, #F3F5F7);
}

.tbl-footer {
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-H6, 1.25rem);
	font-style: normal;
	font-weight: 400;
}

.theme-compact {
	/* global vatables */
    --hcg-font-size: var(--font-size-Base, 1rem);
	--hcg-font-family: var(--font-family-Title, Roboto);
	--hcg-font-weight: 400;
	--hcg-padding: 10px;
	--hcg-vertical-padding: 14px;
    --hcg-border-radius: 0;
	--hcg-caption-font-size: 32px;
	--hgc-color: var(--text-Primary);

	/* section variables */
	--hcg-header-color: var(--text-Primary);
	--hcg-header-background: var(--surface-color-Default);
	--hcg-header-font-weight: 700;
    --hcg-header-vertical-padding: 14px;
	--hcg-header-row-border-width: 2px;
	--hcg-row-border-width: 1px;
	--hcg-row-border-style: solid;
	--hcg-row-border-color: var(--border-color-Divider);
	--hcg-row-even-background:  var(--surface-color-Default);
	--hcg-row-height: 3rem;
	--hcg-row-gap: .5rem;

	--hcg-link-color: var(--text-Link);
}

/* hiding credits */
div.hcg-credits-container,
div.hcg-container .hcg-credits {
	display:none!important;
}

.hc-grid-center {
    text-align: center!important;
}

/* Left justify a column (default, but can be forced) */
.hc-grid-left {
    text-align: left!important;
}

/* Right justify (often used for currency or numeric data) */
.hc-grid-right {
    text-align: right!important;
}

.hcg-table tr:last-of-type td {
	border-bottom: 1px solid rgb(169, 183, 199);
}

.hcg-header-cell-content {
	overflow: visible;
	white-space: normal;
}
.hcg-table thead tr th.hc-grid-left .hcg-header-cell-content  {
	text-align: left !important;
	color: var(--text-Primary);
	font-family: var(--font-family-Title);
	font-weight: 500;
	font-size: var(--font-size-Base);
	word-wrap: break-word;
	overflow-wrap: break-word;
}

.hcg-table thead th .hcg-header-cell-content .table-header-col {
	display:flex;
	flex-direction: row;
	align-items: center;
	width: fit-content;
    min-height: 2.125rem;
}

.hcg-table thead th .hcg-header-cell-content .btn-header-arrows {

	max-height: 1.75rem;
	min-height: 1.75rem;
	display: flex;
	flex-direction: row;
	cursor: pointer;
	color: var(--icon-cta-Blue);
	margin:.25rem .5rem;
	padding:3px;
	border: 0;
    width: 1.5rem;
    min-width: 1.5rem;
	background-color: var(--surface-color-Default);
}
.hcg-table thead th .hcg-header-cell-content .btn-header-arrows:focus {
    outline-width: 1.5px;
    outline-style: solid;
    outline-color: var(--input-background-Active);
    outline-offset: 2px;
	border-radius: 0;
	text-decoration: none;
}
.hcg-table thead th .hcg-header-cell-content .btn-header-arrows .arrow-icon {
  width: 1.25rem;
  background-image: url('@/assets/images/sort-arrows2.svg');
  background-size: contain;
  background-repeat: no-repeat;
}

div.hcg-container.hcg-theme-default.theme-compact .hcg-popup {
	display:none;
}

/* 499px - Mobile */
@media (max-width: 31.1875rem) {
	.tbl-header-container {
	    flex-direction: column;
	    align-items: flex-start;
    	margin-bottom: 1.5rem;
	}
}

/* 1023px - iPad */
.tbl-container {
	container-type: inline-size;
}
@container (max-width: 63.9375rem) {
	.tbl-header-container {
		width: 100%;
	}
}
</style>
